import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSessionActions } from '@/stores/gameSessionStore';
import { QuestionSet, GameSessionSettings, SessionCreationData } from '@/types';

interface SessionCreationWizardProps {
  onSessionCreated?: (sessionId: string, gameCode: string) => void;
  onCancel?: () => void;
}

const SessionCreationWizard = ({ onSessionCreated, onCancel }: SessionCreationWizardProps) => {
  const { user, questionSets, loadQuestionSets } = useAuthStore();
  const { createSession } = useSessionActions();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedQuestionSet, setSelectedQuestionSet] = useState<QuestionSet | null>(null);
  const [sessionSettings, setSessionSettings] = useState<GameSessionSettings>({
    maxPlayers: 999,
    allowLateJoin: true,
    autoAdvance: true,
    enableBonusRounds: true
  });
  const [hasPlayerLimit, setHasPlayerLimit] = useState(false);
  const [customPlayerLimit, setCustomPlayerLimit] = useState(30);
  const [studyPassword, setStudyPassword] = useState('');
  const configuredStudySecret = __WSU_STUDY_SECRET__.trim();
  const isStudySecretConfigured = configuredStudySecret.length > 0;
  const empiricalStudyEnabled =
    isStudySecretConfigured && studyPassword.trim() === configuredStudySecret;

  useEffect(() => {
    loadQuestionSets();
  }, [loadQuestionSets]);

  const totalSteps = 3;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateSession = async () => {
    if (!selectedQuestionSet || !user) return;

    try {
      setIsLoading(true);
      setError(null);

      const sessionData: SessionCreationData = {
        questionSetId: selectedQuestionSet.id,
        settings: {
          ...sessionSettings,
          empiricalStudyEnabled
        }
      };

      const { sessionId, gameCode } = await createSession(sessionData, user.uid);

      console.log('✅ Session created successfully:', { sessionId, gameCode });

      onSessionCreated?.(sessionId, gameCode);
    } catch (err: unknown) {
      console.error('Failed to create session:', err);
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '2rem',
      gap: '1rem'
    }}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '50%',
            background: i + 1 <= currentStep
              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
              : '#e5e7eb',
            color: i + 1 <= currentStep ? 'white' : '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            {i + 1 <= currentStep ? '✓' : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div style={{
              width: '3rem',
              height: '2px',
              background: i + 1 < currentStep
                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                : '#e5e7eb',
              marginLeft: '0.5rem',
              marginRight: '0.5rem'
            }} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div>
      <h3 style={{
        color: '#374151',
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        📚 Select Question Set
      </h3>
      <p style={{
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Choose the question set you want to use for this game session
      </p>

      {questionSets.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
          <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>No question sets available</h4>
          <p>You need to create question sets before starting a game session.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '1rem',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {questionSets.map((questionSet) => (
            <div
              key={questionSet.id}
              onClick={() => setSelectedQuestionSet(questionSet)}
              style={{
                background: selectedQuestionSet?.id === questionSet.id
                  ? 'rgba(59, 130, 246, 0.1)'
                  : 'rgba(255, 255, 255, 0.7)',
                border: selectedQuestionSet?.id === questionSet.id
                  ? '2px solid #3b82f6'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                if (selectedQuestionSet?.id !== questionSet.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseOut={(e) => {
                if (selectedQuestionSet?.id !== questionSet.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <h4 style={{
                  margin: 0,
                  color: '#374151',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {questionSet.name}
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  <span style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {questionSet.totalQuestions} questions
                  </span>
                  <span style={{
                    background: questionSet.difficulty === 'easy' ? '#dcfce7' :
                             questionSet.difficulty === 'medium' ? '#fef3c7' :
                             questionSet.difficulty === 'hard' ? '#fee2e2' : '#f3f4f6',
                    color: questionSet.difficulty === 'easy' ? '#166534' :
                           questionSet.difficulty === 'medium' ? '#92400e' :
                           questionSet.difficulty === 'hard' ? '#991b1b' : '#374151',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {questionSet.difficulty}
                  </span>
                </div>
              </div>
              {questionSet.description && (
                <p style={{
                  color: '#64748b',
                  margin: '0.5rem 0',
                  fontSize: '0.9rem'
                }}>
                  {questionSet.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                fontSize: '0.8rem',
                color: '#64748b',
                marginTop: '1rem'
              }}>
                <span>📅 Created {new Date(questionSet.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 style={{
        color: '#374151',
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        ⚙️ Game Settings
      </h3>
      <p style={{
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Configure how your game session will work
      </p>

      <div style={{
        display: 'grid',
        gap: '1.5rem'
      }}>
        {/* Game Options */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h4 style={{
            color: '#374151',
            marginBottom: '1rem',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            🎮 Game Options
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Allow Late Join */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '0.75rem',
              borderRadius: '8px',
              background: sessionSettings.allowLateJoin ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              border: sessionSettings.allowLateJoin ? '1px solid #3b82f6' : '1px solid transparent'
            }}>
              <input
                type="checkbox"
                checked={sessionSettings.allowLateJoin}
                onChange={(e) => setSessionSettings(prev => ({
                  ...prev,
                  allowLateJoin: e.target.checked
                }))}
                style={{
                  width: '1.2rem',
                  height: '1.2rem',
                  cursor: 'pointer'
                }}
              />
              <div>
                <div style={{ color: '#374151', fontWeight: '600' }}>🚪 Allow Late Join</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Let students join the game after it has started
                </div>
              </div>
            </label>

            {/* Auto Advance */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '0.75rem',
              borderRadius: '8px',
              background: sessionSettings.autoAdvance ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              border: sessionSettings.autoAdvance ? '1px solid #3b82f6' : '1px solid transparent'
            }}>
              <input
                type="checkbox"
                checked={sessionSettings.autoAdvance}
                onChange={(e) => setSessionSettings(prev => ({
                  ...prev,
                  autoAdvance: e.target.checked
                }))}
                style={{
                  width: '1.2rem',
                  height: '1.2rem',
                  cursor: 'pointer'
                }}
              />
              <div>
                <div style={{ color: '#374151', fontWeight: '600' }}>⏩ Auto Advance</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Automatically move to the next question when time runs out
                </div>
              </div>
            </label>

            {/* Enable Bonus Rounds */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '0.75rem',
              borderRadius: '8px',
              background: sessionSettings.enableBonusRounds ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              border: sessionSettings.enableBonusRounds ? '1px solid #3b82f6' : '1px solid transparent'
            }}>
              <input
                type="checkbox"
                checked={sessionSettings.enableBonusRounds}
                onChange={(e) => setSessionSettings(prev => ({
                  ...prev,
                  enableBonusRounds: e.target.checked
                }))}
                style={{
                  width: '1.2rem',
                  height: '1.2rem',
                  cursor: 'pointer'
                }}
              />
              <div>
                <div style={{ color: '#374151', fontWeight: '600' }}>🎁 Enable Bonus Rounds</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Include bonus item selection rounds between questions
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Max Players */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <label style={{
            display: 'block',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '0.5rem'
          }}>
            👥 Maximum Players
          </label>
          <p style={{
            color: '#64748b',
            fontSize: '0.9rem',
            marginBottom: '1rem'
          }}>
            {hasPlayerLimit ? 'Set the maximum number of students who can join' : 'Unlimited players can join this session'}
          </p>

          {/* Unlimited/Limited Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: hasPlayerLimit ? '1.5rem' : '0'
          }}>
            <button
              onClick={() => {
                setHasPlayerLimit(false);
                setSessionSettings(prev => ({ ...prev, maxPlayers: 999 }));
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: !hasPlayerLimit ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.5)',
                color: !hasPlayerLimit ? 'white' : '#64748b',
                border: !hasPlayerLimit ? '2px solid #10b981' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ♾️ Unlimited
            </button>
            <button
              onClick={() => {
                setHasPlayerLimit(true);
                setSessionSettings(prev => ({ ...prev, maxPlayers: customPlayerLimit }));
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: hasPlayerLimit ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'rgba(255, 255, 255, 0.5)',
                color: hasPlayerLimit ? 'white' : '#64748b',
                border: hasPlayerLimit ? '2px solid #3b82f6' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              🔢 Set Limit
            </button>
          </div>

          {/* Custom Limit Input */}
          {hasPlayerLimit && (
            <div>
              <input
                type="range"
                min="5"
                max="100"
                value={customPlayerLimit}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setCustomPlayerLimit(value);
                  setSessionSettings(prev => ({
                    ...prev,
                    maxPlayers: value
                  }));
                }}
                style={{
                  width: '100%',
                  marginBottom: '0.5rem'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>5 players</span>
                <span style={{
                  background: '#3b82f6',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  {customPlayerLimit} players
                </span>
                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>100 players</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={{
        color: '#374151',
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        🚀 Ready to Launch
      </h3>
      <p style={{
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Review your settings and create the game session
      </p>

      <div style={{
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '12px',
        padding: '2rem'
      }}>
        <h4 style={{
          color: '#374151',
          marginBottom: '1.5rem',
          fontSize: '1.1rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          📋 Session Summary
        </h4>

        <div style={{
          display: 'grid',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '8px'
          }}>
            <span style={{ color: '#374151', fontWeight: '600' }}>📚 Question Set:</span>
            <span style={{ color: '#3b82f6', fontWeight: '600' }}>
              {selectedQuestionSet?.name}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem',
            background: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '8px'
          }}>
            <span style={{ color: '#374151', fontWeight: '600' }}>📊 Questions:</span>
            <span style={{ color: '#10b981', fontWeight: '600' }}>
              {selectedQuestionSet?.totalQuestions} questions
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '8px'
          }}>
            <span style={{ color: '#374151', fontWeight: '600' }}>👥 Max Players:</span>
            <span style={{ color: '#f59e0b', fontWeight: '600' }}>
              {sessionSettings.maxPlayers >= 999 ? '♾️ Unlimited' : `${sessionSettings.maxPlayers} students`}
            </span>
          </div>

        </div>

        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'rgba(99, 102, 241, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(99, 102, 241, 0.2)'
        }}>
          <h5 style={{
            color: '#374151',
            margin: '0 0 0.5rem 0',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}>
            ⚙️ Game Options:
          </h5>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {sessionSettings.allowLateJoin && (
              <span style={{
                background: '#dcfce7',
                color: '#166534',
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                🚪 Late Join Allowed
              </span>
            )}
            {sessionSettings.autoAdvance && (
              <span style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                ⏩ Auto Advance
              </span>
            )}
            {sessionSettings.enableBonusRounds && (
              <span style={{
                background: '#fef3c7',
                color: '#92400e',
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                🎁 Bonus Rounds
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '1.5rem',
        background: empiricalStudyEnabled
          ? 'rgba(34, 197, 94, 0.12)'
          : isStudySecretConfigured
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(245, 158, 11, 0.12)',
        borderRadius: '12px',
        padding: '1.5rem',
        border: empiricalStudyEnabled
          ? '1px solid rgba(34, 197, 94, 0.35)'
          : isStudySecretConfigured
            ? '1px solid rgba(0, 0, 0, 0.08)'
            : '1px solid rgba(245, 158, 11, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: '1rem',
          alignItems: 'flex-start'
        }}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '0.5rem'
            }}>
              <h4 style={{
                color: '#374151',
                margin: 0,
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                WSU Empirical Study
              </h4>
              <span style={{
                background: empiricalStudyEnabled
                  ? '#dcfce7'
                  : !isStudySecretConfigured
                    ? '#fef3c7'
                    : studyPassword.trim().length > 0
                      ? '#fee2e2'
                      : '#e5e7eb',
                color: empiricalStudyEnabled
                  ? '#166534'
                  : !isStudySecretConfigured
                    ? '#92400e'
                    : studyPassword.trim().length > 0
                      ? '#b91c1c'
                      : '#4b5563',
                padding: '0.3rem 0.65rem',
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: '700'
              }}>
                {empiricalStudyEnabled
                  ? 'Activated'
                  : !isStudySecretConfigured
                    ? 'Secret Missing'
                    : studyPassword.trim().length > 0
                      ? 'Incorrect Password'
                      : 'Not Active'}
              </span>
            </div>
            <p style={{
              color: empiricalStudyEnabled ? '#166534' : '#64748b',
              margin: 0,
              fontSize: '0.9rem',
              lineHeight: 1.5
            }}>
              Enter the study password to activate the empirical study mode for this game session.
            </p>
            {!isStudySecretConfigured && (
              <p style={{
                color: '#92400e',
                margin: '0.75rem 0 0 0',
                fontSize: '0.8rem',
                lineHeight: 1.5
              }}>
                Add <code>study_secret=...</code> to <code>local.env</code> to enable this option.
              </p>
            )}
          </div>

          <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontWeight: '600',
              marginBottom: '0.5rem',
              fontSize: '0.9rem'
            }}>
              Study Password
            </label>
            <input
              type="password"
              value={studyPassword}
              onChange={(e) => setStudyPassword(e.target.value)}
              placeholder="Enter study password"
              spellCheck={false}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '0.85rem 1rem',
                borderRadius: '10px',
                border: empiricalStudyEnabled
                  ? '2px solid #22c55e'
                  : '1px solid #d1d5db',
                background: empiricalStudyEnabled ? 'rgba(240, 253, 244, 0.95)' : 'white',
                color: '#111827',
                fontSize: '0.95rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '1rem',
          marginTop: '1rem',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedQuestionSet !== null;
      case 2: return true; // Settings have defaults
      case 3: return true;
      default: return false;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            color: '#374151',
            margin: '0 0 0.5rem 0',
            fontSize: '1.5rem',
            fontWeight: '700'
          }}>
            🎮 Create Game Session
          </h2>
          <p style={{
            color: '#64748b',
            margin: 0,
            fontSize: '0.9rem'
          }}>
            Step {currentStep} of {totalSteps}
          </p>
        </div>

        {renderStepIndicator()}
        {renderCurrentStep()}

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={currentStep === 1 ? onCancel : handleBack}
            style={{
              background: 'transparent',
              color: '#64748b',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {currentStep === 1 ? 'Cancel' : '← Back'}
          </button>

          <button
            onClick={currentStep === totalSteps ? handleCreateSession : handleNext}
            disabled={!canProceed() || isLoading}
            style={{
              background: canProceed() && !isLoading
                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: canProceed() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isLoading ? (
              <>🔄 Creating...</>
            ) : currentStep === totalSteps ? (
              <>🚀 Create Session</>
            ) : (
              <>Next →</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionCreationWizard;
